/**
 * Procesos (Module B, P0) — document business processes: a Ficha + a flow
 * table (Fase · Datos Entrada · Procedimiento · Datos Salida · Roles · Destinos)
 * + an editable AI instruction. Gated by features.processes; shows a notice if
 * the org has the module disabled. AI generation + flowchart are B-P1.
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Squares2X2Icon, PlusIcon, TrashIcon, PencilSquareIcon, ChevronLeftIcon,
  ArrowUpIcon, ArrowDownIcon, SparklesIcon, ArrowDownTrayIcon, EyeIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common';
import { processesApi, type AppProcess, type ProcessPhase } from '@/services/api';
import { useOrganizationStore } from '@/stores/organization';
import { PERMISSIONS } from '@/types';

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

const STATUS_LABEL: Record<AppProcess['status'], string> = {
  draft: 'Borrador', review: 'Revisión', active: 'Activo', archived: 'Archivado',
};
const STATUS_COLOR: Record<AppProcess['status'], string> = {
  draft: 'bg-gray-100 text-gray-600', review: 'bg-amber-100 text-amber-700',
  active: 'bg-green-100 text-green-700', archived: 'bg-gray-100 text-gray-400',
};

export default function Processes() {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const canManage = useOrganizationStore((s) => s.hasOrgPermission(PERMISSIONS.PROCESSES_MANAGE));

  const [items, setItems] = useState<AppProcess[]>([]);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [editing, setEditing] = useState<AppProcess | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    setDisabled(false);
    try {
      const r = await processesApi.list();
      setItems(r.data?.data || []);
    } catch (e: unknown) {
      if ((e as { response?: { status?: number } })?.response?.status === 403) setDisabled(true);
      else toast.error('Error al cargar procesos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (selectedOrgId) void load(); }, [selectedOrgId]);

  if (!selectedOrgId) return <div className="p-8 text-center text-gray-500">Selecciona una organización.</div>;
  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400"><LoadingSpinner size="lg" /></div>;

  if (disabled) {
    return (
      <div className="p-8">
        <div className="max-w-xl mx-auto text-center bg-amber-50 border border-amber-200 rounded-xl p-8">
          <Squares2X2Icon className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-gray-900">Procesos no activado</h2>
          <p className="text-sm text-gray-600 mt-2">
            El módulo Procesos no está habilitado para esta organización. Actívalo en la
            configuración de la organización (<code>features.processes</code>).
          </p>
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="p-6">
        <ProcessEditor
          initial={editing === 'new' ? null : editing}
          canManage={canManage}
          onBack={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Squares2X2Icon className="h-7 w-7 text-vida-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Procesos</h1>
            <p className="text-sm text-gray-500">Ficha + tabla de flujo de cada proceso. El flujograma y la IA llegan en la siguiente fase.</p>
          </div>
        </div>
        {canManage && (
          <button onClick={() => setEditing('new')} className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700">
            <PlusIcon className="h-4 w-4" /> Nuevo proceso
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-gray-500">No hay procesos todavía.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Nombre</th>
                <th className="text-left px-4 py-2 font-medium">Estado</th>
                <th className="text-right px-4 py-2 font-medium">Fases</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((p) => (
                <tr key={p._id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setEditing(p)}>
                  <td className="px-4 py-2 font-medium text-gray-900">{p.name}</td>
                  <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[p.status]}`}>{STATUS_LABEL[p.status]}</span></td>
                  <td className="px-4 py-2 text-right text-gray-600">{p.phases?.length || 0}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setEditing(p)} className="p-1.5 text-gray-500 hover:text-gray-900"><PencilSquareIcon className="h-4 w-4" /></button>
                    {canManage && (
                      <button
                        onClick={async () => { try { await processesApi.remove(p._id); setItems((prev) => prev.filter((x) => x._id !== p._id)); } catch { toast.error('Error al eliminar'); } }}
                        className="p-1.5 text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const EMPTY_PHASE: ProcessPhase = { name: '', dataIn: '', procedure: '', dataOut: '', roles: [], destinations: [] };

function ProcessEditor({ initial, canManage, onBack, onSaved }: {
  initial: AppProcess | null; canManage: boolean; onBack: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name || '');
  const [objective, setObjective] = useState(initial?.objective || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [status, setStatus] = useState<AppProcess['status']>(initial?.status || 'draft');
  const [aiInstruction, setAiInstruction] = useState(initial?.aiInstruction || '');
  const [phases, setPhases] = useState<ProcessPhase[]>(initial?.phases?.length ? initial.phases.map((p) => ({ ...EMPTY_PHASE, ...p })) : []);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState<'phases' | 'instruction' | null>(null);
  const [showFlow, setShowFlow] = useState(false);

  const setPhase = (i: number, patch: Partial<ProcessPhase>) => setPhases((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const addPhase = () => setPhases((prev) => [...prev, { ...EMPTY_PHASE }]);
  const removePhase = (i: number) => setPhases((prev) => prev.filter((_, idx) => idx !== i));
  const movePhase = (i: number, dir: number) => setPhases((prev) => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const copy = [...prev];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const aiSuggest = async (target: 'phases' | 'instruction') => {
    if (!name.trim()) { toast.error('Pon primero un nombre al proceso'); return; }
    setAiLoading(target);
    try {
      const res = await processesApi.aiSuggest({ target, name: name.trim(), objective: objective.trim() || undefined, description: description.trim() || undefined });
      if (target === 'instruction') {
        setAiInstruction(res.data?.data?.aiInstruction || '');
        toast.success('Instrucción generada');
      } else {
        const suggested: ProcessPhase[] = (res.data?.data?.phases || []).map((p: ProcessPhase) => ({ ...EMPTY_PHASE, ...p }));
        if (suggested.length === 0) { toast.error('La IA no devolvió fases; reintenta'); return; }
        setPhases((prev) => [...prev, ...suggested]);
        toast.success(`${suggested.length} fases sugeridas (revísalas)`);
      }
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      toast.error(status === 429 ? 'Límite diario de IA alcanzado' : 'Error al generar con IA');
    } finally {
      setAiLoading(null);
    }
  };

  const exportMarkdown = () => {
    const lines: string[] = [];
    lines.push(`# ${name || 'Proceso'}`, '');
    lines.push(`**Estado:** ${STATUS_LABEL[status]}`);
    if (objective.trim()) lines.push(`**Objetivo:** ${objective.trim()}`);
    if (description.trim()) lines.push('', description.trim());
    lines.push('', '## Tabla de flujo', '');
    phases.filter((p) => p.name.trim()).forEach((p, i) => {
      lines.push(`### ${i + 1}. ${p.name}`);
      if (p.dataIn?.trim()) lines.push(`- **Datos entrada:** ${p.dataIn.trim()}`);
      if (p.procedure?.trim()) lines.push(`- **Procedimiento:** ${p.procedure.trim()}`);
      if (p.dataOut?.trim()) lines.push(`- **Datos salida:** ${p.dataOut.trim()}`);
      if (p.roles.length) lines.push(`- **Roles:** ${p.roles.join(', ')}`);
      if (p.destinations.length) lines.push(`- **Destinos:** ${p.destinations.join(', ')}`);
      lines.push('');
    });
    if (aiInstruction.trim()) lines.push('## Instrucción IA', '', aiInstruction.trim(), '');
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(name || 'proceso').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const save = async () => {
    if (!name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    const payload = {
      name: name.trim(), objective: objective.trim() || undefined, description: description.trim() || undefined,
      status, aiInstruction: aiInstruction.trim() || undefined,
      phases: phases
        .filter((p) => p.name.trim())
        .map((p) => ({
          name: p.name.trim(),
          dataIn: p.dataIn?.trim() || undefined,
          procedure: p.procedure?.trim() || undefined,
          dataOut: p.dataOut?.trim() || undefined,
          roles: p.roles.filter(Boolean),
          destinations: p.destinations.filter(Boolean),
        })),
    };
    try {
      if (initial) await processesApi.update(initial._id, payload);
      else await processesApi.create(payload);
      toast.success('Proceso guardado');
      onSaved();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900">
          <ChevronLeftIcon className="h-4 w-4" /> Volver
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowFlow((v) => !v)} className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50">
            <EyeIcon className="h-4 w-4" /> {showFlow ? 'Ocultar' : 'Flujograma'}
          </button>
          <button onClick={exportMarkdown} className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50">
            <ArrowDownTrayIcon className="h-4 w-4" /> Exportar
          </button>
          {canManage && (
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50">
              {saving && <LoadingSpinner size="sm" />} Guardar
            </button>
          )}
        </div>
      </div>

      {showFlow && <Flowchart phases={phases} />}

      {/* Ficha */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <h2 className="text-lg font-bold text-gray-900">Ficha</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className={labelClass}>Nombre *</label>
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} disabled={!canManage} placeholder="Proceso de acogida del cliente" />
          </div>
          <div>
            <label className={labelClass}>Estado</label>
            <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value as AppProcess['status'])} disabled={!canManage}>
              {(['draft', 'review', 'active', 'archived'] as AppProcess['status'][]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className={labelClass}>Objetivo</label>
          <input className={inputClass} value={objective} onChange={(e) => setObjective(e.target.value)} disabled={!canManage} />
        </div>
        <div>
          <label className={labelClass}>Descripción</label>
          <textarea className={inputClass} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} disabled={!canManage} />
        </div>
      </div>

      {/* Tabla de flujo */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-bold text-gray-900">Tabla de flujo</h2>
          {canManage && (
            <div className="flex items-center gap-2">
              <button onClick={() => aiSuggest('phases')} disabled={aiLoading !== null} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-vida-700 bg-vida-50 rounded-lg hover:bg-vida-100 disabled:opacity-50">
                {aiLoading === 'phases' ? <LoadingSpinner size="sm" /> : <SparklesIcon className="h-4 w-4" />} Sugerir fases con IA
              </button>
              <button onClick={addPhase} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">
                <PlusIcon className="h-4 w-4" /> Añadir fase
              </button>
            </div>
          )}
        </div>
        {phases.length === 0 ? (
          <p className="text-sm text-gray-500">Sin fases. Añade la primera (Fase · Datos Entrada · Procedimiento · Datos Salida · Roles · Destinos).</p>
        ) : (
          <div className="space-y-3">
            {phases.map((p, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-400 w-6">{i + 1}</span>
                  <input className={`${inputClass} font-medium`} value={p.name} onChange={(e) => setPhase(i, { name: e.target.value })} placeholder="Fase" disabled={!canManage} />
                  {canManage && (
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button onClick={() => movePhase(i, -1)} className="p-1 text-gray-400 hover:text-gray-700"><ArrowUpIcon className="h-4 w-4" /></button>
                      <button onClick={() => movePhase(i, 1)} className="p-1 text-gray-400 hover:text-gray-700"><ArrowDownIcon className="h-4 w-4" /></button>
                      <button onClick={() => removePhase(i)} className="p-1 text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <textarea className={inputClass} rows={2} value={p.dataIn || ''} onChange={(e) => setPhase(i, { dataIn: e.target.value })} placeholder="Datos de entrada" disabled={!canManage} />
                  <textarea className={inputClass} rows={2} value={p.procedure || ''} onChange={(e) => setPhase(i, { procedure: e.target.value })} placeholder="Procedimiento" disabled={!canManage} />
                  <textarea className={inputClass} rows={2} value={p.dataOut || ''} onChange={(e) => setPhase(i, { dataOut: e.target.value })} placeholder="Datos de salida" disabled={!canManage} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input className={inputClass} value={p.roles.join(', ')} onChange={(e) => setPhase(i, { roles: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="Roles (separados por comas)" disabled={!canManage} />
                  <input className={inputClass} value={p.destinations.join(', ')} onChange={(e) => setPhase(i, { destinations: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="Destinos (separados por comas)" disabled={!canManage} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* (flowchart rendered above when toggled) */}

      {/* Instrucción IA (editable; se puede generar con IA) */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-bold text-gray-900">Instrucción IA</h2>
          {canManage && (
            <button onClick={() => aiSuggest('instruction')} disabled={aiLoading !== null} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-vida-700 bg-vida-50 rounded-lg hover:bg-vida-100 disabled:opacity-50">
              {aiLoading === 'instruction' ? <LoadingSpinner size="sm" /> : <SparklesIcon className="h-4 w-4" />} Generar instrucción con IA
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500">Texto para sistematizar el proceso de cara a la IA. Puedes generarlo y luego editarlo.</p>
        <textarea className={inputClass} rows={5} value={aiInstruction} onChange={(e) => setAiInstruction(e.target.value)} disabled={!canManage} />
      </div>
    </div>
  );
}

/** Simple vertical flowchart derived from the phase order (B-P1). */
function Flowchart({ phases }: { phases: ProcessPhase[] }) {
  const valid = phases.filter((p) => p.name.trim());
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-bold text-gray-900 mb-4">Flujograma</h2>
      {valid.length === 0 ? (
        <p className="text-sm text-gray-500">Añade fases para ver el flujograma.</p>
      ) : (
        <div className="flex flex-col items-center gap-0">
          {valid.map((p, i) => (
            <div key={i} className="w-full max-w-md flex flex-col items-center">
              <div className="w-full border-2 border-vida-200 bg-vida-50/40 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-vida-600 text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="font-semibold text-gray-900">{p.name}</span>
                </div>
                {p.procedure?.trim() && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{p.procedure}</p>}
                {p.roles.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.roles.map((r, ri) => <span key={ri} className="text-[10px] bg-white border border-gray-200 rounded-full px-2 py-0.5 text-gray-600">{r}</span>)}
                  </div>
                )}
                {p.destinations.length > 0 && (
                  <p className="text-[11px] text-gray-400 mt-1">→ {p.destinations.join(' · ')}</p>
                )}
              </div>
              {i < valid.length - 1 && <div className="text-gray-300 text-xl leading-none py-1">↓</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
